import React from 'react';
import { motion } from 'framer-motion';
import { 
  Star, 
  MapPin, 
  TrendingUp, 
  Users, 
  MessageSquare, 
  Sparkles,
  ArrowRight,
  Phone,
  Mail,
  Wrench,
  UtensilsCrossed,
  Stethoscope,
  Home,
  CheckCircle2
} from 'lucide-react';
import './App.css';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } }
};

// Asset URLs - Local images for deployment
const ASSETS = {
  logoMain: "/images/logo-main.png",
  logoAvatar: "/images/logo-avatar.png",
  heroPhone: "/images/hero-phone.jpg",
  localBusiness: "/images/local-business.webp",
  kingsvilleSign: "/images/kingsville-sign.jpg"
};

// Links
const LINKS = {
  cta: "https://diyb.ca/smart-review-management",
  contact: "https://diyb.ca/contact-us",
  facebook: "https://www.facebook.com/FiveStarLocalReviews/"
};

// Star Rating Component
const StarRating = ({ count = 5, size = 20, filled = true }) => (
  <div className="flex gap-1">
    {[...Array(count)].map((_, i) => (
      <Star 
        key={i} 
        size={size} 
        className={filled ? "fill-secondary text-secondary" : "text-gray-300"} 
      />
    ))}
  </div>
);

// Header Component
const Header = () => (
  <header className="fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-md border-b border-border">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between h-16 md:h-20">
        <a href="/" data-testid="header-logo-link" className="flex items-center">
          <img 
            src={ASSETS.logoMain} 
            alt="Five Star Local Reviews" 
            className="h-10 md:h-14 w-auto"
          />
        </a>
        <motion.a
          href={LINKS.cta}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="header-cta-button"
          className="btn-primary text-sm md:text-base"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Get Started
          <ArrowRight size={18} className="ml-2" />
        </motion.a>
      </div>
    </div>
  </header>
);

// Hero Section
const HeroSection = () => (
  <section className="pt-24 md:pt-32 pb-16 md:pb-24 bg-background relative overflow-hidden">
    {/* Subtle background pattern */}
    <div className="absolute inset-0 opacity-5">
      <div className="absolute top-20 left-10 w-64 h-64 bg-primary rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-secondary rounded-full blur-3xl"></div>
    </div>
    
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left Content */}
        <motion.div 
          className="order-2 lg:order-1"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="mb-6">
            <StarRating count={5} size={28} />
          </motion.div>
          
          <motion.h1 
            variants={fadeInUp}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-foreground leading-[1.1] tracking-tight mb-6"
            data-testid="hero-headline"
          >
            Stop Chasing Customers for Reviews.{' '}
            <span className="text-primary">Let Them Come to You.</span>
          </motion.h1>
          
          <motion.p 
            variants={fadeInUp}
            className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 max-w-xl"
            data-testid="hero-subhead"
          >
            You do the hard work. Let us make sure everyone knows about it. 
            Automatically collect 5-star reviews from your happy customers in 
            Kingsville, Ontario and Windsor-Essex County Area.
          </motion.p>
          
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
            <motion.a
              href={LINKS.cta}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-primary"
              className="btn-primary text-lg"
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              Start Getting More Reviews
              <ArrowRight size={20} className="ml-2" />
            </motion.a>
          </motion.div>
          
          <motion.div variants={fadeInUp} className="mt-8 flex items-center gap-3">
            <div className="flex -space-x-2">
              <div className="w-10 h-10 rounded-full bg-primary-light border-2 border-card flex items-center justify-center">
                <Users size={18} className="text-primary" />
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Trusted by local businesses in <span className="font-medium text-foreground">Kingsville, Ontario and Windsor-Essex County Area</span>
            </p>
          </motion.div>
        </motion.div>
        
        {/* Right Visual */}
        <motion.div 
          className="order-1 lg:order-2 relative"
          initial="hidden"
          animate="visible"
          variants={scaleIn}
        >
          <div className="relative">
            {/* Main Image */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src={ASSETS.heroPhone}
                alt="Happy customer leaving a 5-star review on their phone"
                className="w-full h-auto max-h-[50vh] lg:max-h-[60vh] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/20 to-transparent"></div>
            </div>
            
            {/* Floating Avatar Badge */}
            <motion.div 
              className="absolute -bottom-6 -left-6 md:-bottom-8 md:-left-8"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
              <div className="bg-card p-2 rounded-2xl shadow-xl border border-border">
                <img 
                  src={ASSETS.logoAvatar}
                  alt="Five Star Local Reviews Badge"
                  className="w-20 h-20 md:w-28 md:h-28"
                />
              </div>
            </motion.div>
            
            {/* Floating Review Card */}
            <motion.div 
              className="absolute -top-4 -right-4 md:-top-6 md:-right-6 bg-card p-4 rounded-xl shadow-xl border border-border"
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
            >
              <StarRating count={5} size={16} />
              <p className="text-xs mt-2 font-medium text-foreground">"Excellent service!"</p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

// Problem Section
const ProblemSection = () => (
  <section className="py-20 md:py-28 bg-card" data-testid="problem-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div 
        className="max-w-3xl mx-auto text-center"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <motion.div variants={fadeInUp} className="mb-6">
          <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium">
            <Star size={16} className="text-gray-400" />
            The Problem
          </div>
        </motion.div>
        
        <motion.h2 
          variants={fadeInUp}
          className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-8"
          data-testid="problem-headline"
        >
          Great Service Doesn't Always Mean Great Reviews
        </motion.h2>
        
        <motion.div variants={fadeInUp} className="space-y-6 text-left md:text-center">
          <p className="text-lg text-muted-foreground leading-relaxed">
            You finish a job. The customer is thrilled. They shake your hand and say{' '}
            <span className="font-medium text-foreground">"Great work!"</span>
          </p>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Then they walk away. Life gets busy. They forget to leave a review. 
            You feel awkward asking for one days later. That 5-star experience{' '}
            <span className="font-medium text-accent">disappears</span>.
          </p>
          <p className="text-lg text-muted-foreground leading-relaxed">
            When neighbors search for your services online, they see your competitors first. 
            Not because they are better.{' '}
            <span className="font-medium text-foreground">They just have more reviews.</span>
          </p>
          <p className="text-xl font-medium text-foreground mt-8">
            It costs you business every single day.
          </p>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

// Solution Section with Feature Grid
const SolutionSection = () => {
  const features = [
    {
      icon: MessageSquare,
      title: "Automated Requests",
      description: "One click sends a polite text or email to your customer. They click a link. You get the stars. No awkward conversations required.",
      color: "primary"
    },
    {
      icon: Sparkles,
      title: "AI-Powered Replies",
      description: "Google loves it when you reply to reviews. It shows you care. Our system helps you write professional responses in seconds using smart AI tools.",
      color: "secondary"
    },
    {
      icon: TrendingUp,
      title: "Showcase Your Success",
      description: "Don't hide your best feedback. Automatically display your latest 5-star reviews on your website to build trust with new visitors immediately.",
      color: "accent"
    }
  ];

  return (
    <section className="py-20 md:py-28 bg-background" data-testid="solution-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="mb-6">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">
              <CheckCircle2 size={16} />
              The Solution
            </div>
          </motion.div>
          
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-6"
            data-testid="solution-headline"
          >
            Turn Happy Customers Into Your{' '}
            <span className="text-primary">Best Sales Team</span>
          </motion.h2>
          
          <motion.p variants={fadeInUp} className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Five Star Local Reviews makes reputation management automatic. 
            We provide the tools you need to capture feedback while the experience is fresh.
          </motion.p>
        </motion.div>
        
        <motion.div 
          className="grid md:grid-cols-3 gap-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="feature-card group"
              data-testid={`feature-card-${index}`}
            >
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 ${
                feature.color === 'primary' ? 'bg-primary/10' :
                feature.color === 'secondary' ? 'bg-secondary/20' :
                'bg-accent/10'
              }`}>
                <feature.icon 
                  size={28} 
                  className={
                    feature.color === 'primary' ? 'text-primary' :
                    feature.color === 'secondary' ? 'text-secondary-dark' :
                    'text-accent'
                  } 
                />
              </div>
              <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground mb-4">
                {feature.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Stats/Why It Matters Section
const StatsSection = () => {
  const stats = [
    { value: "88%", label: "of people trust online reviews as much as a friend's recommendation" },
    { value: "Higher", label: "Google rankings with a steady flow of fresh, positive reviews" },
    { value: "More $", label: "Customers pay more for services with a proven track record" }
  ];

  return (
    <section className="py-20 md:py-28 bg-primary text-white relative overflow-hidden" data-testid="stats-section">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary rounded-full blur-3xl"></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div 
          className="text-center mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold mb-6"
            data-testid="stats-headline"
          >
            Reviews Are The New Front Door
          </motion.h2>
          
          <motion.p variants={fadeInUp} className="text-lg text-white/80 max-w-2xl mx-auto">
            Before a customer calls you, they check your score. Your reputation is your most valuable asset.
          </motion.p>
        </motion.div>
        
        <motion.div 
          className="grid md:grid-cols-3 gap-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="text-center p-8 rounded-2xl bg-white/10 backdrop-blur-sm"
              data-testid={`stat-card-${index}`}
            >
              <div className="text-4xl md:text-5xl font-heading font-bold text-secondary mb-4">
                {stat.value}
              </div>
              <p className="text-white/90 leading-relaxed">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Who We Help Section
const WhoWeHelpSection = () => {
  const industries = [
    { icon: Wrench, title: "Trades & Contractors", description: "Build a portfolio of trust that wins high-value jobs." },
    { icon: UtensilsCrossed, title: "Restaurants", description: "Capture feedback instantly after a meal." },
    { icon: Stethoscope, title: "Medical & Dental", description: "Show new patients they are in safe hands." },
    { icon: Home, title: "Real Estate", description: "Stand out in a crowded market with client testimonials." }
  ];

  return (
    <section className="py-20 md:py-28 bg-card" data-testid="who-we-help-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Image Side */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={scaleIn}
            className="relative"
          >
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img 
                src={ASSETS.localBusiness}
                alt="Local business in Kingsville Ontario"
                className="w-full h-auto max-h-[50vh] object-cover"
                data-testid="who-we-help-image"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 md:-bottom-6 md:-right-6 bg-card p-4 rounded-xl shadow-xl border border-border">
              <MapPin size={24} className="text-accent mb-2" />
              <p className="text-sm font-medium text-foreground">Kingsville, Ontario</p>
            </div>
          </motion.div>
          
          {/* Content Side */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="mb-6">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">
                <Users size={16} />
                Who We Help
              </div>
            </motion.div>
            
            <motion.h2 
              variants={fadeInUp}
              className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-6"
              data-testid="who-we-help-headline"
            >
              Built for <span className="text-primary">Local Business</span>
            </motion.h2>
            
            <motion.p variants={fadeInUp} className="text-lg text-muted-foreground mb-10">
              We work with business owners right here in Kingsville, Ontario and Windsor-Essex County Area who need practical solutions that actually work.
            </motion.p>
            
            <motion.div variants={staggerContainer} className="grid sm:grid-cols-2 gap-8">
              {industries.map((industry, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="flex items-start gap-4"
                  data-testid={`industry-${index}`}
                >
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <industry.icon size={28} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-heading text-xl md:text-2xl font-semibold text-foreground mb-2">
                      {industry.title}
                    </h4>
                    <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                      {industry.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Trust/Local Section
const TrustSection = () => (
  <section className="py-20 md:py-28 bg-background" data-testid="trust-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Content Side */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="mb-6">
            <div className="inline-flex items-center gap-2 bg-secondary/20 text-secondary-dark px-4 py-2 rounded-full text-sm font-medium">
              <MapPin size={16} />
              Local & Trusted
            </div>
          </motion.div>
          
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-6"
            data-testid="trust-headline"
          >
            We're Neighbors.{' '}
            <span className="text-primary">Not Just a Tech Company.</span>
          </motion.h2>
          
          <motion.div variants={fadeInUp} className="space-y-6">
            <p className="text-lg text-muted-foreground leading-relaxed">
              Five Star Local Reviews is powered by{' '}
              <a href="https://diyb.ca" target="_blank" rel="noopener noreferrer" className="font-semibold text-foreground hover:text-primary transition-colors underline decoration-primary/30 hover:decoration-primary">Doorways Into Your Business</a>. 
              We are based right here in Kingsville, Ontario.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              We know the challenges you face because we are in your shoes. 
              We don't just sell you software and disappear. We help you set it up. 
              We train your team. We make sure it works for your specific business.
            </p>
            <p className="text-xl font-medium text-foreground">
              Your success is our success.
            </p>
          </motion.div>
          
          <motion.div variants={fadeInUp} className="mt-8 flex items-center gap-4">
            <img 
              src={ASSETS.logoAvatar}
              alt="Five Star Local Reviews"
              className="w-16 h-16 rounded-xl"
            />
            <div>
              <p className="font-heading font-semibold text-foreground">Five Star Local Reviews</p>
              <a href="https://diyb.ca" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-primary transition-colors">Part of the <span className="underline decoration-primary/30 hover:decoration-primary">Doorways Into Your Business</span> Family</a>
            </div>
          </motion.div>
        </motion.div>
        
        {/* Image Side */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={scaleIn}
          className="relative"
        >
          <div className="rounded-3xl overflow-hidden shadow-xl">
            <img 
              src={ASSETS.kingsvilleSign}
              alt="Kingsville Ontario - Canada's Most Southern Town"
              className="w-full h-auto max-h-[50vh] object-cover"
              data-testid="trust-image"
            />
          </div>
          <motion.div 
            className="absolute -top-4 -left-4 md:-top-6 md:-left-6 bg-card p-4 rounded-xl shadow-xl border border-border"
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          >
            <StarRating count={5} size={18} />
            <p className="text-xs mt-2 text-muted-foreground">Local & Trusted</p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  </section>
);

// Final CTA Section
const FinalCTASection = () => (
  <section className="py-20 md:py-28 bg-card" data-testid="final-cta-section">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <motion.div variants={fadeInUp} className="mb-8">
          <StarRating count={5} size={32} />
        </motion.div>
        
        <motion.h2 
          variants={fadeInUp}
          className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-6"
          data-testid="final-cta-headline"
        >
          Ready to Open <span className="text-primary">New Doors</span>?
        </motion.h2>
        
        <motion.p variants={fadeInUp} className="text-lg text-muted-foreground mb-10 max-w-2xl mx-auto">
          Every day you wait is another missed review. Take control of your reputation today.
        </motion.p>
        
        <motion.div variants={fadeInUp}>
          <motion.a
            href={LINKS.cta}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="final-cta-button"
            className="btn-primary text-lg inline-flex"
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
          >
            See How It Works
            <ArrowRight size={20} className="ml-2" />
          </motion.a>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

// Footer Component
const Footer = () => (
  <footer className="py-12 md:py-16 bg-foreground text-white" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-3 gap-10 md:gap-8">
        {/* Brand */}
        <div>
          <img 
            src={ASSETS.logoMain}
            alt="Five Star Local Reviews"
            className="h-12 w-auto mb-4 brightness-0 invert"
          />
          <p className="text-white/70 text-sm leading-relaxed">
            Part of the <a href="https://diyb.ca" target="_blank" rel="noopener noreferrer" className="hover:text-white underline decoration-white/30 hover:decoration-white transition-colors">Doorways Into Your Business</a> Family.
          </p>
        </div>
        
        {/* Contact */}
        <div>
          <h4 className="font-heading font-semibold text-lg mb-4">Contact</h4>
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-white/70">
              <MapPin size={18} className="text-secondary flex-shrink-0" />
              <span className="text-sm">Kingsville, Ontario</span>
            </div>
            <div className="flex items-center gap-3 text-white/70">
              <Phone size={18} className="text-secondary flex-shrink-0" />
              <a 
                href="tel:2267733857" 
                className="text-sm hover:text-white transition-colors"
                data-testid="footer-phone"
              >
                (226) 773-3857
              </a>
            </div>
          </div>
        </div>
        
        {/* Links */}
        <div>
          <h4 className="font-heading font-semibold text-lg mb-4">Links</h4>
          <div className="space-y-3">
            <a 
              href={LINKS.contact}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-sm text-white/70 hover:text-white transition-colors"
              data-testid="footer-contact-link"
            >
              Contact Us
            </a>
            <a 
              href={LINKS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-sm text-white/70 hover:text-white transition-colors"
              data-testid="footer-facebook-link"
            >
              Follow us on Facebook
            </a>
            <a 
              href={LINKS.cta}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-sm text-white/70 hover:text-white transition-colors"
              data-testid="footer-cta-link"
            >
              Smart Review Management
            </a>
          </div>
        </div>
      </div>
      
      <div className="mt-12 pt-8 border-t border-white/10 text-center">
        <p className="text-white/50 text-sm">
          © {new Date().getFullYear()} Five Star Local Reviews & <a href="https://diyb.ca" target="_blank" rel="noopener noreferrer" className="hover:text-white/70 underline decoration-white/20 hover:decoration-white/50 transition-colors">Doorways Into Your Business</a>. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

// Main App Component
function App() {
  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <HeroSection />
        <ProblemSection />
        <SolutionSection />
        <StatsSection />
        <WhoWeHelpSection />
        <TrustSection />
        <FinalCTASection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
